/**
 * ActivityRail — persistent right-side rail showing activity feed.
 *
 * Replaces FloatingTransactionPanel. Resizable via left-edge drag handle.
 * Width persisted to localStorage, clamped 200–400px.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Money } from '@/components/atoms/Money';
import { useActivity } from '@/contexts/ActivityContext';
import type { ActivityEventType } from '@/contexts/ActivityContext';
import { resolveDisplayTitle } from '@/lib/kalshiTicker/displayTitle';
import {
  deriveConnectionStatus,
  formatTime,
  statusDot,
  statusLabel,
  eventTypeLabel,
  eventTypeColor,
} from '@/lib/activityHelpers';

type ActivityFilter = 'all' | 'my_orders' | 'market_tape' | 'sweeps';

const MY_ORDER_TYPES: ActivityEventType[] = ['order_placed', 'order_filled', 'order_cancelled'];
const MARKET_TAPE_TYPES: ActivityEventType[] = ['market_trade'];
const SWEEP_TYPES: ActivityEventType[] = ['liquidity_sweep'];

const WIDTH_KEY = 'activity-rail-width';
const MIN_WIDTH = 200;
const MAX_WIDTH = 400;
const DEFAULT_WIDTH = 280;

function readPersistedWidth(): number {
  try {
    const raw = localStorage.getItem(WIDTH_KEY);
    if (raw) {
      const n = parseInt(raw, 10);
      if (n >= MIN_WIDTH && n <= MAX_WIDTH) return n;
    }
  } catch {
    /* noop */
  }
  return DEFAULT_WIDTH;
}

export function ActivityRail() {
  const { events, clear } = useActivity();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(readPersistedWidth);
  const [isResizing, setIsResizing] = useState(false);
  const [filter, setFilter] = useState<ActivityFilter>('all');
  /** When true, auto-scroll is active (user hasn't scrolled up). */
  const autoScrollRef = useRef(true);

  const txEvents = useMemo(() => {
    const noVelocity = events.filter((e) => e.type !== 'queue_velocity');
    switch (filter) {
      case 'my_orders':
        return noVelocity.filter((e) => MY_ORDER_TYPES.includes(e.type));
      case 'market_tape':
        return noVelocity.filter((e) => MARKET_TAPE_TYPES.includes(e.type));
      case 'sweeps':
        return noVelocity.filter((e) => SWEEP_TYPES.includes(e.type));
      default:
        return noVelocity;
    }
  }, [events, filter]);

  const connectionStatus = deriveConnectionStatus(events);

  // Auto-scroll to top on new events (events are newest-first)
  useEffect(() => {
    if (autoScrollRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [txEvents.length]);

  // Detect manual scroll to disable auto-scroll
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // If user scrolled away from top, disable auto-scroll
    autoScrollRef.current = el.scrollTop < 10;
  }, []);

  // Resize drag handling
  const widthRef = useRef(width);
  widthRef.current = width;

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    const startX = e.clientX;
    const startWidth = widthRef.current;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      // Dragging left increases width, dragging right decreases
      const delta = startX - moveEvent.clientX;
      const next = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startWidth + delta));
      setWidth(next);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  // Persist width on change (debounced by not persisting during drag)
  useEffect(() => {
    if (!isResizing) {
      try {
        localStorage.setItem(WIDTH_KEY, String(width));
      } catch {
        /* noop */
      }
    }
  }, [width, isResizing]);

  return (
    <div
      className="bg-muted border-border/50 relative flex flex-shrink-0 flex-col border-l"
      style={{ width: `${width}px` }}
    >
      {/* Resize handle */}
      <div
        className={cn(
          'absolute inset-y-0 left-0 w-1 cursor-col-resize transition-colors',
          isResizing ? 'bg-primary/50' : 'hover:bg-primary/30'
        )}
        onMouseDown={handleMouseDown}
      />

      {/* Connection status header */}
      <div className="border-border/50 border-b px-3 py-2">
        <div className="flex items-center gap-2">
          <div className={cn('h-2 w-2 rounded-full', statusDot[connectionStatus])} />
          <span className="text-muted-foreground text-xs">{statusLabel[connectionStatus]}</span>
          <span className="text-muted-foreground ml-auto text-xs tabular-nums">
            {txEvents.length}
          </span>
          {txEvents.length > 0 && (
            <button
              onClick={clear}
              className="text-muted-foreground hover:text-foreground ml-1 transition-colors"
              title="Clear activity history"
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
        {/* Filter chips */}
        <div className="mt-1.5 flex gap-1">
          {(
            [
              ['all', 'All'],
              ['my_orders', 'My Orders'],
              ['market_tape', 'Tape'],
              ['sweeps', 'Sweeps'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={cn(
                'rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors',
                filter === key
                  ? 'bg-primary/20 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable event list */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto"
        onScroll={handleScroll}
      >
        {txEvents.length === 0 ? (
          <div className="text-muted-foreground px-3 py-8 text-center text-xs">No activity yet</div>
        ) : (
          txEvents.map((event) => {
            const isTape = event.type === 'market_trade';
            return (
              <div
                key={event.id}
                className={cn(
                  'border-border/20 flex items-start gap-2 border-b px-3 text-xs',
                  isTape ? 'py-0.5 opacity-60' : 'py-1.5'
                )}
              >
                <span
                  className={cn(
                    'mt-0.5 flex-shrink-0 font-mono text-[10px] uppercase',
                    eventTypeColor(event.type)
                  )}
                >
                  {eventTypeLabel(event.type)}
                </span>
                <div className="min-w-0 flex-1">
                  <span
                    className={cn(
                      'font-medium',
                      isTape ? 'text-muted-foreground' : 'text-foreground'
                    )}
                  >
                    {event.ticker && event.message.includes(event.ticker)
                      ? event.message.replace(event.ticker, resolveDisplayTitle(event.ticker))
                      : event.message}
                  </span>
                  {event.amount !== undefined && !isTape && (
                    <>
                      <span className="text-muted-foreground mx-1">·</span>
                      <Money
                        value={event.amount}
                        className="text-xs"
                      />
                    </>
                  )}
                </div>
                <span className="text-muted-foreground mt-0.5 flex-shrink-0 whitespace-nowrap text-[10px]">
                  {formatTime(event.timestamp)}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
